import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useFormContext } from 'react-hook-form';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { UploadService } from '../../../../api/upload.service';
import ConfirmDialog from '../../../../components/confirm-dialog';

type Props = {
  canvas?: string;
  side: string;
  setCanvas: (value: string) => void;
  type?: string;
};

const AddCanvas = ({ canvas, side, setCanvas, type }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setValue } = useFormContext();
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [lastUploadOk, setLastUploadOk] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const canvasName = `${side}-${type === 'external' ? 'eye' : 'lens'}`;

  const visualField =
    type === 'external'
      ? (`externalEyeHealth.${
          side === 'left' ? 'leftVisualImage' : 'rightVisualImage'
        }` as const)
      : (`ophthalmoscopy.${
          side === 'left' ? 'leftVisualImage' : 'rightVisualImage'
        }` as const);

  useEffect(() => {
    if (!pendingFile) {
      setLocalPreview(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setLocalPreview(url);
    setLastUploadOk(false);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  // Removing an image used to happen on the first click with no confirmation,
  // so a stored examination image could be lost by accident (client bug 32).
  const handleRemove = () => {
    setConfirmRemoveOpen(false);
    setPendingFile(null);
    setLocalPreview(null);
    setLastUploadOk(false);
    setCanvas('');
    setValue(visualField, '', { shouldDirty: true });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please choose an image file', { variant: 'warning' });
      return;
    }
    setPendingFile(file);
  };

  const handleUpload = async () => {
    if (!pendingFile) {
      enqueueSnackbar('Choose an image first', { variant: 'info' });
      return;
    }
    try {
      setUploading(true);
      const uploadService = new UploadService();
      const id = `${canvasName}-${Date.now()}`;
      const response = await uploadService.uploadFile(
        pendingFile,
        'examination',
        id,
      );
      const url = response.data?.url as string | undefined;
      if (url) {
        setCanvas(url);
        setValue(visualField, url);
        setPendingFile(null);
        setLastUploadOk(true);
        enqueueSnackbar('Image uploaded', { variant: 'success' });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Image upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const displaySrc = localPreview || canvas;

  return (
    <Box sx={{ p: 3, maxWidth: 640, margin: '0 auto' }}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Choose an image, confirm the preview, then click Upload.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            data-lock-hide
            variant="outlined"
            component="label"
            disabled={uploading}
          >
            Choose image
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={handleFileChange}
            />
          </Button>

          {pendingFile ? (
            <LoadingButton
              variant="contained"
              onClick={handleUpload}
              loading={uploading}
              disabled={uploading}
            >
              Upload
            </LoadingButton>
          ) : null}
        </Stack>

        {lastUploadOk && !pendingFile && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">
              Image uploaded successfully. It will be kept when you save the
              examination form.
            </Typography>
          </Stack>
        )}

        {displaySrc ? (
          <Stack spacing={1} alignItems="center">
            <Typography variant="subtitle2">Preview</Typography>
            <Box
              component="img"
              src={displaySrc}
              alt="Examination"
              referrerPolicy="no-referrer"
              sx={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            />
          </Stack>
        ) : null}

        {displaySrc && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              data-lock-hide
              color="error"
              onClick={() => setConfirmRemoveOpen(true)}
              disabled={uploading}
            >
              Remove image
            </Button>
          </Stack>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        title="Remove image"
        content={
          pendingFile
            ? 'Discard the image you just chose? It has not been uploaded yet.'
            : 'Remove this image from the examination? It is cleared when you save the form.'
        }
        action={
          <Button variant="contained" color="error" onClick={handleRemove}>
            Remove
          </Button>
        }
      />
    </Box>
  );
};

export default AddCanvas;
